/* eslint-disable @typescript-eslint/no-explicit-any */
import { CircularProgress, Divider } from '@mui/material'
import { GET_CONTENTS, getContentsListSelect } from 'api'
import { getTagsInfo } from 'api/get/reach/reach'
import clsx from 'clsx'
import { Button, Modal, TagAudience, TitleBox } from 'components'
import { ListTags } from 'components/confirmationModal/listTags'
import { TitleContent } from 'components/confirmationModal/titleContent'
import { TitleContentRecurrence } from 'components/confirmationModal/titleContentRecurrence'
import { useSearchDebounce } from 'hooks'
import { DetailNotification, Icons } from 'interfaces'
import React, { useState } from 'react'
import { useInfiniteQuery, useQuery } from 'react-query'
import { useNavigate } from 'react-router-dom'
import Cookies from 'universal-cookie'
import { formatLocalTime } from 'utils'

interface CalendarPanelModalProps {
	viewMore: boolean
	setViewMore: (value: boolean) => void
	data: DetailNotification
	dataAudience: any
	isLoading: boolean
	isLoadingLocation: boolean
	isLoadingAudience: boolean
	organizationId: number
	hasAudience: boolean
	hasTags: boolean
	hasStaticLists: boolean
	hasDynamicLists: boolean
	hasIndividuals: boolean
	handleCopy: (url: string) => void
	id: number
	isEdit: boolean
}

export const CalendarPanelModalPush: React.FC<CalendarPanelModalProps> = ({
	viewMore,
	setViewMore,
	data,
	dataAudience,
	isLoading,
	isLoadingLocation,
	isLoadingAudience,
	organizationId,
	hasAudience,
	hasTags,
	hasStaticLists,
	hasDynamicLists,
	hasIndividuals,
	handleCopy,
	id,
	isEdit
}) => {
	const numberElement = 10
	const [isOpenTagBoxModal, setIsOpenTagBoxModal] = useState(false)
	const [page, setPage] = useState<number>(1)
	const [valueItemsTags, setValueItemsTags] = useState(numberElement)
	const [searchTags, setSearchTags] = useState('')
	const navigate = useNavigate()

	const {
		inputSearch: inputSearchTags,
		handleChangeDebounce: handleChangeDebounceTags,
		resetInput: resetInputTags
	} = useSearchDebounce('')

	const {
		data: dataTagBox,
		isLoading: isLoadingTagBox,
		hasNextPage: hasNextPageTagsReach,
		fetchNextPage: fetchNextPageTagsReach,
		isFetchingNextPage: isFetchingNextPageTagsReach
	} = useInfiniteQuery({
		queryKey: [
			'GET_TAGS_BOX',
			data?.audience?.tags,
			organizationId,
			page,
			valueItemsTags,
			inputSearchTags
		],
		queryFn: ({ pageParam = 1 }) =>
			getTagsInfo(
				data?.audience?.tags as number[],
				organizationId,
				pageParam,
				valueItemsTags,
				inputSearchTags
			),
		getNextPageParam: response => {
			const nextPage = Number(response?.page) + 1
			if (nextPage <= Number(response?.pages)) {
				return nextPage
			}
			return undefined
		},
		enabled:
			data &&
			data?.audience?.tags &&
			data.audience.tags.length > 0 &&
			isOpenTagBoxModal,
		refetchOnWindowFocus: false
	})

	const hasDeliveryEnhancements =
		data?.custom_recurrence?.repeating_schedule || data?.cta_type
	const cookies = new Cookies()

	const userId = cookies.get('userId')

	const { data: lisContent } = useQuery({
		queryKey: [GET_CONTENTS, organizationId, userId],
		queryFn: () =>
			getContentsListSelect(organizationId, '', null, {
				content_status: 'published'
			}),
		refetchOnWindowFocus: false,
		enabled: !!data?.cta_value
	})

	return (
		<Modal
			marginL
			closeButton
			type="normal"
			maxWidth="max-w-[70vw]"
			open={viewMore}
			title="Preview"
			setOpen={setViewMore}
			icon={Icons.preview}
			className="z-[2001]"
		>
			<>
				<div className="overflow-y-auto max-h-[70vh]">
					{isLoading || isLoadingLocation || isLoadingAudience ? (
						<div className="flex h-full w-full justify-center items-center">
							<CircularProgress disableShrink />
						</div>
					) : (
						<>
							{/* Push Details */}
							<div className="border border-blue-primary rounded-lg p-4 my-4">
								<div className="flex items-stretch gap-4">
									<div className="border border-blue-primary rounded-lg p-4 flex-grow flex flex-col h-full min-h-[197px] min-w-[200px]">
										<div className="flex-grow">
											<p className="text-[15px] text-black-1 font-bold font-Inter">
												{data?.title}
											</p>
											<hr className="my-2" />
											<div
												className="text-[15px] text-black-1 font-normal font-Inter"
												// eslint-disable-next-line react/no-danger
												dangerouslySetInnerHTML={{
													__html: data?.message || ''
												}}
											/>
										</div>
									</div>
								</div>
							</div>

							{/* Delivery Enhancements */}
							{hasDeliveryEnhancements && (
								<div className="border border-blue-primary rounded-lg p-4 my-4">
									{data?.custom_recurrence?.repeating_schedule && (
										<>
											<p className="text-[15px] text-gray-18 font-bold font-Inter">
												Push Recurrence
											</p>
											<TitleContentRecurrence
												viewTitle={false}
												hasDivider={false}
												type="push"
												endTitle={
													data?.custom_recurrence?.ends?.repeat_end_type ===
													'after_ocurrence'
														? 'after'
														: 'on'
												}
												publish={formatLocalTime(
													data?.timing.release_date as string,
													'MM/DD/YYYY'
												)}
												recurrent={
													data?.timing?.release_date === null ||
													data?.timing?.release_date === ''
												}
												endsRecurrent={
													data?.custom_recurrence?.ends?.repeat_end_type ===
													'never'
														? 'Never'
														: data?.custom_recurrence?.ends?.repeat_end_type ===
														  'after_ocurrence'
														? `${data?.custom_recurrence.ends.after_ocurrences} Occurrences`
														: formatLocalTime(
																data?.custom_recurrence?.ends
																	?.repeat_end_date as string,
																'MM/DD/YYYY'
														  )
												}
												publishRecurrent={formatLocalTime(
													data?.custom_recurrence.repeat_start_date as string,
													'MM/DD/YYYY'
												)}
												// eslint-disable-next-line max-len
												repeatEveryRecurrent={`${data?.custom_recurrence.repeat_every_value} ${data?.custom_recurrence.repeat_every_type}`}
												repeatOnRecurrent={formatLocalTime(
													data?.custom_recurrence.repeat_on as string,
													'MM/DD/YYYY'
												)}
												listDaysRecurrence={
													data?.custom_recurrence.repeat_every_type === 'week'
														? data?.custom_recurrence.repeat_on
																// eslint-disable-next-line no-useless-escape
																.replace(/[\[\]']+/g, '')
																.split(',')
														: undefined
												}
											/>
										</>
									)}
									<div className="flex items-center justify-between gap-2">
										<TitleContent
											isFull
											noBorder
											title="Link to:"
											content={data?.cta_type?.toUpperCase() || 'None'}
											content2={
												data?.cta_type !== 'content'
													? data?.cta_value ?? undefined
													: lisContent?.items?.find(
															e => e.id_content.toString() === data?.cta_value
													  )?.title
											}
										>
											<Button
												onClick={() => handleCopy(data?.cta_value as string)}
												iconLeft={Icons.contentCopy}
												iconClassName="w-[24px] h-[24px]"
												color="Primary"
												className="!min-w-[unset] !border-none"
											/>
										</TitleContent>
									</div>
								</div>
							)}

							{/* Audience Section */}
							<div className="border border-blue-primary rounded-lg p-4 my-4">
								<div className="flex flex-row justify-between items-center gap-2">
									<TitleBox icon={Icons.audience} title="Audience:" />
									<p
										className={clsx(
											`text-[22px] text-white font-normal font-Inter border border-blue-primary bg-blue-primary rounded-full px-2 ${
												dataAudience?.reach &&
												dataAudience?.reach.toString().length === 1
													? 'w-[44px] h-[44px] flex items-center justify-center'
													: 'max-w-max'
											} capitalize mb-4`
										)}
									>
										{dataAudience?.reach || 0}
									</p>
								</div>

								<div className="overflow-y-auto max-h-[300px]">
									{hasAudience && (
										<>
											{hasTags && (
												<>
													<TitleContent
														title="Tags:"
														content={
															dataAudience?.detailed_reach?.tag.total.toString() ||
															'0'
														}
														noBorder
														isFull
														isBig
														onClickContent={() => {
															setIsOpenTagBoxModal(true)
														}}
													/>
													<ListTags
														listTags={
															dataAudience?.detailed_reach?.tag.list || []
														}
													/>
													<Divider />
												</>
											)}
											{hasStaticLists && (
												<>
													<TitleContent
														title="Static Lists:"
														content={
															dataAudience?.detailed_reach?.static_list.total.toString() ||
															'0'
														}
														noBorder
														isFull
														isBig
													/>
													<ListTags
														listTags={
															dataAudience?.detailed_reach?.static_list.list ||
															[]
														}
													/>
													<Divider />
												</>
											)}
											{hasDynamicLists && (
												<>
													<TitleContent
														title="Dynamic Lists:"
														content={
															dataAudience?.detailed_reach?.dynamic_list.total.toString() ||
															'0'
														}
														noBorder
														isFull
														isBig
													/>
													<ListTags
														listTags={
															dataAudience?.detailed_reach?.dynamic_list.list ||
															[]
														}
													/>
													<Divider />
												</>
											)}
											{hasIndividuals && (
												<>
													<TitleContent
														title="Individuals:"
														content={
															dataAudience?.detailed_reach?.individual.total.toString() ||
															'0'
														}
														noBorder
														isFull
														isBig
													/>
													<ListTags
														listTags={
															dataAudience?.detailed_reach?.individual.list ||
															[]
														}
													/>
												</>
											)}
										</>
									)}

									{!hasAudience && !isLoading && (
										<TitleContent
											title=" All of My People:"
											content={dataAudience?.reach?.toString() ?? '0'}
											noBorder
											isFull
											isBig
										/>
									)}
								</div>
							</div>
							{isEdit && (
								<div className="flex items-center justify-end mt-4 gap-6">
									<Button
										type="button"
										label="Edit"
										color="Primary"
										iconRight={Icons.edit}
										className="!min-w-[130px]"
										onClick={() =>
											navigate(`/content/push-notification/edit/${id}`)
										}
									/>
								</div>
							)}
						</>
					)}
				</div>

				{/* Tags Box Modal */}
				{isOpenTagBoxModal && (
					<Modal
						setOpen={setIsOpenTagBoxModal}
						open={isOpenTagBoxModal}
						type="normal"
						sizeModal="h-[418px] p-2"
						maxWidth="max-w-[302px]"
						marginL
					>
						<TagAudience
							resetInput={resetInputTags}
							handleChangeDebounce={handleChangeDebounceTags}
							setSearchTags={setSearchTags}
							searchTags={searchTags}
							dataTagsAudience={dataTagBox}
							isLoadingTagsAudience={isLoadingTagBox}
							page={page}
							setPage={setPage}
							valueItems={valueItemsTags}
							setValueItems={setValueItemsTags}
							fetchNextPageTagsReach={fetchNextPageTagsReach}
							hasNextPageTagsReach={hasNextPageTagsReach}
							isFetchingNextPageTagsReach={isFetchingNextPageTagsReach}
						/>
					</Modal>
				)}
			</>
		</Modal>
	)
}
